import { Component, inject, Input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '@shared/services/api.service';
import { formatDate } from '@shared/utils/format-date';
import { BasicForm } from '@shared/components/classes/basic.form';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ReadingService } from '@modules/universal-billing/reading/reading.service';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { CalendarModule } from '@primeng/calendar';
import { InputValidatorDirective } from '@common/directive/input-validator.directive';
import { ModalClose } from "@shared/services/modal/modal.enum";
import { SweetAlertService } from '@shared/services/sweet-alert.service';

@Component({
  selector: 'app-meter-readings',
  standalone: true,
  templateUrl: './meter-readings.component.html',
  styleUrls: ['./meter-readings.component.scss'],
  imports: [
    ReactiveFormsModule,
    CommonModule,
    TranslatePipe,
    SpinnerComponent,
    CalendarModule,
    InputValidatorDirective,
  ],
})
export class MeterReadingsComponent extends BasicForm<any> {
  @Input() declare selectedRow: any;

  public form: FormGroup = new FormGroup({
    readingDate: new FormControl(null, [Validators.required]),
    readingValue: new FormControl(null, [Validators.required]),
  });

  public readings: { readingDate: string; readingValue: number; billed: boolean; isNew: boolean; id?: number }[] = [];
  public errorMessage: string | null = null;
  public isReadingsLoading: boolean = false;
  public isSearchReadingsLoading: boolean = false;
  private hasDeletedReadings: boolean = false;

  override _defaultService: any = inject(ReadingService);

  protected api = inject(ApiService);
  protected translateService = inject(TranslateService);
  protected sweetAlertService = inject(SweetAlertService);

  ngOnInit(): void {
    this.loadReadings();
  }

  loadReadings(): void {
    this.isReadingsLoading = true;

    this.api.get(`/readings`, { meter_id: this.selectedRow.id }).subscribe(
      {
        next: (res) => {
          this.isReadingsLoading = false;
          this.readings = res.data.data
            .filter((reading: any) => !reading.billed)
            .map((reading: any) => ({ ...reading, isNew: false }))
            .sort((a: any, b: any) => new Date(a.readingDate).getTime() - new Date(b.readingDate).getTime());
        },
        error: (error) => {
          this.isPending.set(false);
          this.errorMessage = error.message || this.translateService.instant('ERRORS.FETCH_FAILED');
        }
      },
    );
  }

  addReading(): void {
    if (this.form.valid) {
      const newReadingDate = formatDate(this.form.value.readingDate);

      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const inputDate = new Date(this.form.value.readingDate);

      if (inputDate > today) {
        this.errorMessage = this.translateService.instant('ERRORS.READING_DATE_FUTURE');
        return;
      }

      const existingReading = this.readings.find(
        (reading) => reading.readingDate === newReadingDate
      );

      if (existingReading) {
        this.errorMessage = this.translateService.instant('ERRORS.READING_DATE_EXISTS');
        return;
      }

      const reading = {
        readingDate: newReadingDate,
        readingValue: this.form.value.readingValue,
        billed: false,
        isNew: true,
      };

      this.readings.push(reading);
      this.sortReadingsByDate();
      this.form.reset({ readingDate: new Date(), readingValue: null });
      this.errorMessage = null;
    } else {
      this.errorMessage = this.translateService.instant('ERRORS.INVALID_READING');
    }
  }

  saveReadings(): void {
    const newReadings = this.readings.filter((reading) => reading.isNew);

    if (newReadings.length === 0 && !this.hasDeletedReadings) {
      this.errorMessage = this.translateService.instant('ERRORS.NO_NEW_READINGS');
      this.isPending.set(false);
      return;
    }

    if (newReadings.length === 0 && this.hasDeletedReadings) {
      this.isPending.set(false);
      this.errorMessage = null;
      this._modalService.close(ModalClose.SUCCESS);
      this.hasDeletedReadings = false;
      return;
    }

    const meterId = this.selectedRow?.id;

    this.errorMessage = null;
    this.isPending.set(true);

    this.api.post(`/meters/${meterId}/readings/bulk`, { readings: newReadings }).subscribe(
      () => {
        this.isPending.set(false);
        this.readings.forEach((reading) => (reading.isNew = false));
        this.errorMessage = null;
        this.form.reset({ readingDate: new Date(), readingValue: null });
        this.afterSuccessSubmit(null);
        this._modalService.close(ModalClose.SUCCESS);
        this.hasDeletedReadings = false;
      },
      (error) => {
        this.isPending.set(false);
        this.readings = this.readings.filter(reading => !reading.isNew);
        this.errorMessage = error.error.message || this.translateService.instant('ERRORS.SAVE_FAILED');
      }
    );
  }

  getMeterReading(): void {
    const readingDateValue = this.form.value.readingDate;
    if (!this.selectedRow || !readingDateValue) {
      this.errorMessage = this.translateService.instant('ERRORS.READING_DATE_MISSING');
      return;
    }

    const meterId = this.selectedRow?.id;
    const formattedDate = formatDate(readingDateValue, 'yyyy-MM-dd');

    this.errorMessage = null;
    this.isSearchReadingsLoading = true;
    this.api.get(`/meters/${meterId}/readings`, { date: formattedDate }).subscribe(
      (res) => {
        this.isSearchReadingsLoading = false;
        if (res.data) {
          const readingData = Array.isArray(res.data)
            ? res.data.find(r => r.readingDate === formattedDate)
            : res.data.readingDate === formattedDate ? res.data : null;

          if (readingData) {
            const reading = {
              ...readingData,
              isNew: true,
              billed: readingData.billed ?? false
            };
            this.form.patchValue({ readingValue: reading.readingValue });

            const existingReading = this.readings.find(
              (r) => r.readingDate === reading.readingDate
            );
            if (!existingReading) {
              this.readings.push(reading);
              this.sortReadingsByDate();
            } else {
              this.errorMessage = this.translateService.instant('ERRORS.READING_DATE_EXISTS');
            }
          } else {
            this.errorMessage = this.translateService.instant('ERRORS.NO_READING');
          }
        } else {
          this.errorMessage = this.translateService.instant('ERRORS.NO_READING');
        }
      },
      (error) => {
        this.isSearchReadingsLoading = false;
        this.errorMessage = error.message || this.translateService.instant('ERRORS.FETCH_FAILED');
      }
    );
  }

  private sortReadingsByDate(): void {
    this.readings.sort((a, b) => new Date(a.readingDate).getTime() - new Date(b.readingDate).getTime());
  }

  removeReading(index: number): void {
    if (index >= 0 && index < this.readings.length) {
      const reading = this.readings[index];

      this.hasDeletedReadings = true;

      if (reading.id && !reading.isNew) {
        this.isPending.set(true);
        this.api.delete(`/readings/${reading.id}`).subscribe(
          () => {
            this.readings.splice(index, 1);
            this.isPending.set(false);
            this.errorMessage = null;
            this.sweetAlertService.success(null, 'TOAST.TOAST_UPDATE');
          },
          (error) => {
            this.isPending.set(false);
            this.errorMessage = error.error?.message || this.translateService.instant('ERRORS.DELETE_FAILED');
            console.error('Delete reading error:', error);
            console.error('Backend error details:', {
              status: error.status,
              message: error.error?.message,
              url: `/readings/${reading.id}`,
            });
          }
        );
      } else {
        this.readings.splice(index, 1);
        this.errorMessage = null;
        this.isPending.set(false);
        this.sweetAlertService.success(null, 'TOAST.OPERATION_WAS_SUCCESSFULLY_COMPLETED');
      }
    }
  }
}
